import { IssueNavigationState, Location } from './types';
import { CommonTranslationKeys } from 'intl/keys';
import {
  isCreatePath,
  isEditPath,
  isInspectionChecklistPath,
  isInspectionDetailsPath as isInspectionDetailsPathByRoute,
  isInspectionEditPath,
  isInspectionListPath as isInspectionListPathByRoute,
  isInspectionTemplatePath,
  isIssueDetailsPath as isIssueDetailsPathByRoute,
} from 'setup/navigation/routeMatchers';
import { isDocumentationPath } from 'setup/navigation/paths';

interface PreviousLocationsSnapshot {
  prevLocation: Location | undefined;
  prevPrevLocation: Location | undefined;
}

export function isIssueDetailsPath(pathname: string): boolean {
  return isIssueDetailsPathByRoute(pathname);
}

export function getIssueNavigationState(
  state: unknown
): IssueNavigationState {
  return (state as IssueNavigationState | null) ?? {};
}

function getReturnUrlPathname(returnUrl: string): string {
  const queryIndex = returnUrl.indexOf('?');
  return queryIndex === -1 ? returnUrl : returnUrl.slice(0, queryIndex);
}

export function isIssueListReturnUrl(returnUrl: unknown): returnUrl is string {
  if (typeof returnUrl !== 'string') {
    return false;
  }

  return (
    returnUrl.startsWith('/issue') ||
    isDocumentationPath(getReturnUrlPathname(returnUrl))
  );
}

export function getPreviousLocationsSnapshot(
  locationsHistory: Location[]
): PreviousLocationsSnapshot {
  const historyLength = locationsHistory.length;

  return {
    prevLocation:
      historyLength > 1 ? locationsHistory[historyLength - 2] : undefined,
    prevPrevLocation:
      historyLength > 2 ? locationsHistory[historyLength - 3] : undefined,
  };
}

export function popLastTwoLocations(locationsHistory: Location[]): void {
  locationsHistory.pop();
  locationsHistory.pop();
}

export function isEditOrCreateOrTemplatePath(
  pathname?: string
): boolean {
  if (!pathname) {
    return false;
  }

  return (
    isEditPath(pathname) ||
    isCreatePath(pathname) ||
    isInspectionTemplatePath(pathname)
  );
}

export function shouldRedirectChecklistToInspectionEdit(
  prevLocation: Location | undefined,
  wasInEditOrCreate: boolean
): boolean {
  return Boolean(
    prevLocation &&
      isInspectionChecklistPath(prevLocation.pathname) &&
      wasInEditOrCreate
  );
}

export function getInspectionEditPathFromLocation(
  pathname: string
): string | null {
  const inspectionIdMatch = pathname.match(/\/inspection\/([^\/]+)/);
  const projectIdMatch = pathname.match(/\/project\/([^\/]+)/);

  if (!inspectionIdMatch || !projectIdMatch) {
    return null;
  }

  return `/project/${projectIdMatch[1]}/inspection/${inspectionIdMatch[1]}/edit`;
}

export function isRelevantLocationChange(
  location: Location,
  lastLocations: Location[]
): boolean {
  if (location.pathname === '/') {
    return false;
  }
  const prevLocation = lastLocations.slice(-1)[0];
  if (!prevLocation) {
    return true;
  }
  return (
    prevLocation.pathname !== location.pathname ||
    prevLocation.search !== location.search
  );
}

export function canReturnToLocation(location: Location): boolean {
  return !isCreatePath(location.pathname);
}

export function resolveTitle(
  currentLocation: Location,
  lastLocations: Location[]
): string | undefined {
  if (lastLocations.length > 1) {
    return CommonTranslationKeys.back;
  }
  return undefined;
}

export function buildUrl(location: Location): string {
  if (location.search) {
    return `${location.pathname}${location.search}`;
  }
  return location.pathname;
}

export function isInspectionDetailsPage(pathname: string): boolean {
  return isInspectionDetailsPathByRoute(pathname);
}

export function isInspectionListPage(pathname: string): boolean {
  return isInspectionListPathByRoute(pathname);
}

function findLatestInspectionListLocation(
  locationsHistory: Location[]
): Location | undefined {
  // Start before the last two entries: [.., previous, current]
  for (let i = locationsHistory.length - 3; i >= 0; i -= 1) {
    const candidate = locationsHistory[i];
    if (isInspectionListPathByRoute(candidate.pathname)) {
      return candidate;
    }
  }

  return undefined;
}

export function extractInspectionId(pathname: string): string | null {
  const match = pathname.match(/\/inspection\/([^\/]+)/);
  return match ? match[1] : null;
}

export function isEditOfSameInspection(
  currentPath: string,
  previousLocation: Location | undefined
): boolean {
  if (!previousLocation) {
    return false;
  }

  const isPreviousLocationEdit = isInspectionEditPath(
    previousLocation.pathname
  );

  if (!isPreviousLocationEdit) {
    return false;
  }

  const currentInspectionId = extractInspectionId(currentPath);
  const previousInspectionId = extractInspectionId(previousLocation.pathname);

  return (
    currentInspectionId !== null &&
    previousInspectionId !== null &&
    currentInspectionId === previousInspectionId
  );
}

export function handleReplaceLocation(
  newLocation: Location,
  locationsHistory: Location[]
): Location[] {
  const currentPath = newLocation.pathname;

  // Requirement: after saving an inspection (edit -> details with replace),
  // clicking the header back should go straight to the inspection list,
  // never back to edit or create.
  if (isInspectionDetailsPage(currentPath) && locationsHistory.length > 1) {
    const updatedHistory = [...locationsHistory];
    const latestInspectionListLocation =
      findLatestInspectionListLocation(locationsHistory);

    updatedHistory[updatedHistory.length - 1] = newLocation;
    updatedHistory[updatedHistory.length - 2] =
      latestInspectionListLocation ?? {
        pathname: '/inspection',
        state: undefined,
      };
    return updatedHistory;
  }
  
  // Normal replace behavior - just replace the last location
  const updatedHistory = [...locationsHistory];
  updatedHistory[updatedHistory.length - 1] = newLocation;
  return updatedHistory;
}
